/* =============================================================================
   Solva — bundle app bridge

   Loaded after solva.js. It teaches <solva-product-form> to share the product
   form with a third-party bundle app instead of fighting it.

   Why this exists
     Rapi Bundles renders its offer bars *inside* this theme's
     {% form 'product' %} and works by writing to that form. It does not add to
     the cart itself — it relies on the theme submitting the form it updated.

     Left alone, <solva-product-form> conflicted with that in two ways:
       1. render() force-wrote `quantity` from its own bundle-tier state on
          every variant change, discarding the app's quantity.
       2. It multiplied the variant price by that quantity, so the page
          advertised three units at full price while the app was charging the
          discounted bundle price.

   What this file changes
     — While a bundle app is present the theme stops writing `quantity` and
       leaves the app as its single owner.
     — The theme's price block shows the per-unit variant price; the sticky bar
       shows the chosen offer's bundle total. The two are never mixed and no
       price is hard-coded — both come from live data.
     — Add to cart is disabled only for a missing variant, a genuinely
       sold-out variant, or a request already in flight. Never for a price
       change, a bundle change, or a discount.
     — One add-to-cart request per click, through the theme's existing AJAX
       cart, carrying whatever the app placed in the form.

   Detection is a live DOM query rather than a cached flag, so the app
   injecting or re-rendering its widget at any time is handled for free.
   ============================================================================= */
(function () {
  'use strict';

  var BUS = 'solva:variant:change';

  var BUNDLE_SELECTOR = [
    '.rapi-bundles',
    '[name^="rapi-bundles-deal"]',
    '[name="properties[_rapi_bundles]"]',
    '[data-bundle-app]'
  ].join(',');

  var DEAL_NAME = 'rapi-bundles-deal';
  var BAR_SELECTOR = '.rapi-bundles__bar';

  /* Money elements the app emits. `.rapi-money` is its own; `.money` is the
     convention almost every money-formatting script uses. */
  var MONEY_SELECTOR = '.rapi-money, .money';

  function toNumber(text) {
    // Strip everything that is not a digit or separator, then normalise so
    // 1.234,50 and 1,234.50 both come out as 1234.5.
    var raw = String(text).replace(/[^\d.,]/g, '');
    if (!raw) return NaN;
    if (raw.indexOf(',') > -1 && raw.indexOf('.') > -1) {
      raw = raw.lastIndexOf(',') > raw.lastIndexOf('.')
        ? raw.replace(/\./g, '').replace(',', '.')
        : raw.replace(/,/g, '');
    } else if (raw.indexOf(',') > -1) {
      raw = /,\d{3}\b/.test(raw) ? raw.replace(/,/g, '') : raw.replace(',', '.');
    }
    return parseFloat(raw);
  }

  /* Read the price the app itself prints on a chosen offer.

     An offer bar carries two figures — the payable total and a compare-at
     ($59.48 beside $94.10). Take the lower: what a customer pays can never
     exceed what it is compared against, which holds however the app orders or
     styles them. More durable than keying on undocumented class names or on a
     strikethrough that may be applied by a parent.

     Restricted to the app's own money elements so it cannot pick up a label
     that merely contains a digit — an earlier version of this matched "Buy 2".

     Returns null rather than guessing, so the caller falls back to live
     Shopify data. */
  function readOfferPrice(bar) {
    if (!bar) return null;
    var nodes = bar.querySelectorAll(MONEY_SELECTOR);
    var best = null;
    var bestValue = Infinity;
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.children.length) continue;
      var text = (el.textContent || '').replace(/\s+/g, ' ').trim();
      if (!text || text.indexOf('%') > -1) continue;
      var value = toNumber(text);
      if (!isFinite(value) || value <= 0) continue;
      if (value < bestValue) {
        bestValue = value;
        best = text;
      }
    }
    return best;
  }

  function ready(fn) {
    if (window.customElements && window.customElements.whenDefined) {
      window.customElements.whenDefined('solva-product-form').then(fn);
    } else {
      fn();
    }
  }

  ready(function () {
    var ctor = window.customElements.get('solva-product-form');
    if (!ctor || ctor.prototype.__solvaBundleBridge) return;
    var proto = ctor.prototype;
    proto.__solvaBundleBridge = true;

    proto.bundleAppActive = function () {
      var scope = this.form || this;
      return !!scope.querySelector(BUNDLE_SELECTOR);
    };

    /* Re-resolved rather than cached, so an app replacing part of the buy area
       can never leave this holding a detached node. */
    proto.atcButton = function () {
      return this.querySelector('[data-atc]');
    };

    /* ---- quantity ---------------------------------------------------------
       Submitted quantity: whatever is in the form. With a bundle app that is
       the app's value; without one it is the theme's stepper or tier.
       Displayed quantity: 1 while a bundle app is active, so the theme's price
       block keeps showing the honest per-unit variant price. */
    Object.defineProperty(proto, 'quantity', {
      configurable: true,
      get: function () {
        var live = parseInt(this.qtyInput && this.qtyInput.value, 10);
        if (this.bundleAppActive()) return Math.max(1, live || 1);
        var tier = this.selectedTier();
        if (tier) return parseInt(tier.dataset.tierQty, 10) || 1;
        return Math.max(1, live || 1);
      }
    });

    Object.defineProperty(proto, 'displayQuantity', {
      configurable: true,
      get: function () {
        return this.bundleAppActive() ? 1 : this.quantity;
      }
    });

    /* ---- the chosen offer -------------------------------------------------
       Two ways of finding it, because the app checks a hidden radio in some
       builds and only paints a class on the bar in others. */
    proto.selectedBundleBar = function () {
      var scope = this.form || this;
      var checked = scope.querySelector('[name^="' + DEAL_NAME + '"]:checked');
      if (checked && checked.closest) {
        var bar = checked.closest(BAR_SELECTOR);
        if (bar) return bar;
      }
      return scope.querySelector(
        BAR_SELECTOR + '[aria-checked="true"],' +
        BAR_SELECTOR + '[class*="select"],' +
        BAR_SELECTOR + '[class*="active"]'
      );
    };

    /* ---- sticky total -----------------------------------------------------
       What the sticky bar shows. The app's own printed figure is preferred
       because it is the only one already carrying the offer's discount. When
       no offer is chosen, or the figure cannot be read, this falls back to
       live Shopify variant price times the submitted quantity. Both paths
       follow an Admin price change; neither contains a literal price. */
    proto.bundleTotal = function () {
      if (this.bundleAppActive()) {
        var shown = readOfferPrice(this.selectedBundleBar());
        if (shown) return shown;
      }
      return this.priceFor(this.current, this.quantity).total;
    };

    /* Re-announce the total.

       Two safeguards, both learned the hard way on this page:

       — Deduplicated. The widget contains elements that repaint on their own,
         so the observer below can fire repeatedly with nothing actually
         changed. Bailing when the string is identical turns that storm into a
         string comparison. Pass true to force, after a real variant change.

       — mediaId is deliberately null. <solva-gallery> listens on this same
         event and smooth-scrolls the stage whenever it receives a media id.
         A price refresh must never move the customer's gallery, and doing so
         on every repaint is what made the page judder. */
    proto.broadcastTotal = function (force) {
      var total = this.bundleTotal();
      if (!force && total === this.__solvaLastTotal) return;
      this.__solvaLastTotal = total;

      var variant = this.current;
      var selected = this.selectedOptions();
      var labels = Object.keys(selected).map(function (k) { return selected[k]; });
      var qty = this.quantity;

      /* Write the sticky bar directly, as well as dispatching.

         <solva-sticky-atc> subscribes to the event, and for an ordinary
         variant change that is enough. It is not enough here for two reasons:
         this call exists to *correct* a total the base render has already
         announced, so the order the two announcements arrive in decides what
         the customer ends up seeing; and the sticky element upgrades
         independently of this one, so on a slow page it can still be waiting
         when the first announcement goes out. Writing the node is idempotent,
         costs one query, and makes the visible result independent of listener
         timing. The event is still dispatched for every other subscriber. */
      var stickyPrice = document.querySelector('[data-sticky-price]');
      if (stickyPrice && stickyPrice.textContent !== total) {
        stickyPrice.textContent = total;
      }

      var stickyMeta = document.querySelector('[data-sticky-meta]');
      if (stickyMeta) {
        var meta = [labels.join(' · '), qty > 1 ? '× ' + qty : '']
          .filter(Boolean)
          .join(' · ');
        if (stickyMeta.textContent !== meta) stickyMeta.textContent = meta;
      }

      document.dispatchEvent(
        new CustomEvent(BUS, {
          detail: {
            sectionId: this.sectionId,
            variant: variant,
            quantity: qty,
            total: total,
            available: !!(variant && variant.available),
            mediaId: null,
            optionsLabel: labels.join(' · ')
          }
        })
      );
    };

    /* ---- render -----------------------------------------------------------
       Runs the original render, then repairs what a bundle app must not have
       had done to it, and re-broadcasts with the bundle total so the sticky
       bar agrees with the offer on screen. */
    var baseRender = proto.render;
    proto.render = function () {
      var bundled = this.bundleAppActive();
      var keepQty = this.qtyInput ? this.qtyInput.value : null;
      var showTotal = this.dataset.showTotal;

      if (bundled) this.dataset.showTotal = 'false';

      baseRender.call(this);

      if (!bundled) return;

      this.dataset.showTotal = showTotal;

      if (this.qtyInput && keepQty !== null && this.qtyInput.value !== keepQty) {
        this.qtyInput.value = keepQty;
      }

      // Per-unit price, never quantity-multiplied, never bundle-derived.
      var unit = this.priceFor(this.current, 1);
      this.setText('[data-price-now]', unit.total);
      this.setText('[data-price-was]', unit.compare);
      this.setNode('[data-price-was]', unit.compare !== null);
      this.setText('[data-price-save]', unit.saveLabel);
      this.setNode('[data-price-save]', !!unit.saveLabel);
      this.setNode('[data-price-unit]', false);

      this.applyAvailability();

      // baseRender already broadcast a unit-times-quantity total. Correct it.
      this.broadcastTotal(true);
    };

    /* ---- availability -----------------------------------------------------
       The only three reasons to disable: no variant matched, the variant is
       genuinely sold out, or a request is in flight. */
    proto.applyAvailability = function () {
      var submit = this.atcButton();
      if (!submit) return;
      var variant = this.current;
      var available = !!(variant && variant.available);
      submit.disabled = !available || !!this.inFlight;

      var label = submit.querySelector('[data-atc-label]') || submit;
      if (this.inFlight) {
        label.textContent = this.dataset.loadingText || 'Adding…';
      } else if (!variant) {
        label.textContent = this.dataset.unavailableText || 'Unavailable';
      } else if (!available) {
        label.textContent = this.dataset.soldOutText || 'Sold out';
      } else {
        label.textContent = this.dataset.addText || 'Add to cart';
      }
    };

    /* ---- submit -----------------------------------------------------------
       One controlled add per click. The body is read straight off the live
       form, so the app's quantity and line item properties travel with it. */
    var baseSubmit = proto.onSubmit;
    proto.onSubmit = function (event) {
      if (this.inFlight) {
        if (event && event.preventDefault) event.preventDefault();
        return;
      }
      if (this.behaviour !== 'page') {
        this.inFlight = true;
        if (this.bundleAppActive()) this.applyAvailability();
      }
      baseSubmit.call(this, event);
    };

    var baseBusy = proto.setBusy;
    proto.setBusy = function (busy) {
      if (!busy) this.inFlight = false;
      baseBusy.call(this, busy);
      if (this.bundleAppActive()) this.applyAvailability();
    };

    /* ---- staying in sync --------------------------------------------------
       Three triggers, deliberately unequal in weight.

       change on an offer radio — the real signal. The app's radios live inside
       the form, so this bubbles here: no polling, and no listener on the app's
       own elements, which it re-renders. Deferred a frame so the app applies
       its own state first and we read what it settled on, not what it was
       mid-update. This is the only trigger that runs a full render.

       click inside an offer bar — a safety net, because the app marks its
       radios readonly and a readonly control does not always emit change. This
       one deliberately does NOT re-render: it only re-reads the price and
       writes it, so it cannot perturb the app's own selection handling. An
       earlier version called render() here and that appeared to disturb the
       app mid-update.

       MutationObserver — the app repaints its prices asynchronously after a
       variant change and fires no event for it. Kept safe three ways: scoped
       to the bars container rather than the whole widget, so unrelated live
       elements inside the widget do not wake it; coalesced to one animation
       frame; and its only action is a deduplicated broadcast that exits on a
       string comparison when the price has not moved. It writes nothing inside
       the subtree it watches, so it cannot feed itself. */
    function watch(el) {
      if (el.__solvaBundleWatch) return;
      el.__solvaBundleWatch = true;

      var scope = el.form || el;

      scope.addEventListener('change', function (e) {
        var t = e.target;
        if (!t || !t.name || t.name.indexOf(DEAL_NAME) !== 0) return;
        requestAnimationFrame(function () { el.render(); });
      });

      scope.addEventListener('click', function (e) {
        var t = e.target;
        if (!t || !t.closest || !t.closest(BAR_SELECTOR)) return;
        requestAnimationFrame(function () { el.broadcastTotal(); });
      });

      if (!('MutationObserver' in window)) return;
      var widget = scope.querySelector('.rapi-bundles__bars') || scope.querySelector('.rapi-bundles');
      if (!widget) return;

      var queued = false;
      var obs = new MutationObserver(function () {
        if (queued) return;
        queued = true;
        requestAnimationFrame(function () {
          queued = false;
          el.broadcastTotal();
        });
      });
      obs.observe(widget, { childList: true, subtree: true, characterData: true });
    }

    /* ---- first paint ------------------------------------------------------
       The app injects its widget after this element has upgraded and rendered
       once, so that first render ran before the bundle existed. Watch for the
       widget and re-render once, then stop. The observer disconnects itself
       the moment the widget appears, and a load-time cap clears it on pages
       with no bundle app, so nothing is left running. */
    function primeOnce(el) {
      if (el.bundleAppActive()) {
        el.render();
        watch(el);
        return;
      }
      if (!('MutationObserver' in window) || !el.form) return;
      var obs = new MutationObserver(function () {
        if (!el.bundleAppActive()) return;
        obs.disconnect();
        el.render();
        watch(el);
      });
      obs.observe(el.form, { childList: true, subtree: true });
      window.addEventListener(
        'load',
        function () {
          setTimeout(function () { obs.disconnect(); }, 5000);
        },
        { once: true }
      );
    }

    Array.prototype.slice.call(document.querySelectorAll('solva-product-form')).forEach(primeOnce);
  });
})();
